import { API } from 'aws-amplify';

const isCorporateMode = process.env.REACT_APP_CORPORATE_MODE === 'true';
const api_name = isCorporateMode
    ? "Corporate-API"
    : (process.env.REACT_APP_STAGE === 'DEV' ? "Node-API" : "prod-api");

// Get an existing annual review by employee email and id
export const getAnnualReview = async (email, id) => {
  try {
    const res = await API.post(api_name, '', {
      body: {
        command: 'getPerformanceReview',
        email,
        reviewType: 'annual',
        ...(id ? { id } : {}),
      },
      response: true,
      convertEmptyValues: true,
      headers: {},
    });
    if (res?.status === 200 && res?.data) {
      try { return JSON.parse(res.data.body); } catch { return res.data; }
    }
    return null;
  } catch (error) {
    return null;
  }
};

// Backward-compat alias
export const getReview = getAnnualReview;

// Get all annual performance reviews
export const getAllReviews = async () => {
  try {
    const res = await API.post(api_name, '', {
      body: {
        command: 'getAllPerformanceReviews',
        reviewType: 'annual',
      },
      response: true,
      convertEmptyValues: true,
      headers: {},
    });
    if (res?.status === 200 && res?.data) {
      try { return JSON.parse(res.data.body); } catch { return res.data; }
    }
    return { reviews: [] };
  } catch (error) {
    console.error('Error getting all annual reviews:', error);
    return { reviews: [] };
  }
};

// Create a new annual review row for the employee (self review flow)
const addAnnualReview = async ({ email, user_response, periodStart, periodEnd, formId }) => {
  const now = new Date().toISOString();
  const payload = {
    command: 'addPerformanceReview',
    id: `${email}_annual_${periodStart}`,
    email,
    reviewType: 'annual',
    form_id: formId || '',
    user_response,
    manager_response: '',
    second_level_manager_response: '',
    period_start: periodStart,
    period_end: periodEnd,
    created_at: now,
    updated_at: now,
  };
  return API.post(api_name, '', { body: payload, response: true, convertEmptyValues: true, headers: {} });
};

// Update only the user_response JSON string
const updateUserAnnualResponse = async ({ id, email, user_response }) => {
  const payload = {
    command: 'updateUserPerformanceResponse',
    id,
    email,
    reviewType: 'annual',
    user_response,
    updated_at: new Date().toISOString(),
  };
  return API.post(api_name, '', { body: payload, response: true, convertEmptyValues: true, headers: {} });
};

// Public API: Save self annual review as JSON in user_response; create-or-update per period
export const saveSelfReview = async (inputs) => {
  try {
    const email = inputs.userEmail;
    const periodStart = inputs.periodStart;
    const periodEnd = inputs.periodEnd;
    const formId = inputs.formId || '';

    const user_response = inputs.responseData
      ? JSON.stringify(inputs.responseData)
      : JSON.stringify(inputs);

    const id = `${email}_annual_${periodStart}`;
    const existing = await getAnnualReview(email, id);

    if (!existing || !existing.review) {
      return await addAnnualReview({ email, user_response, periodStart, periodEnd, formId });
    }
    return await updateUserAnnualResponse({ id, email, user_response });
  } catch (error) {
    console.error('Error saving self annual review:', error);
    throw error;
  }
};

// Update only the manager_response JSON string for the selected employee
export const saveManagerReview = async (inputs) => {
  try {
    const email = inputs.employeeEmail;
    const manager_response = inputs.responseData
      ? JSON.stringify(inputs.responseData)
      : JSON.stringify(inputs);
    const payload = {
      command: 'updateManagerPerformanceResponse',
      id: inputs.employeeId || inputs.id || email,
      email,
      reviewType: 'annual',
      manager_response,
      updated_at: new Date().toISOString(),
    };
    return await API.post(api_name, '', { body: payload, response: true, convertEmptyValues: true, headers: {} });
  } catch (error) {
    console.error('Error saving manager annual review:', error);
    throw error;
  }
};

// Helper to check whether a manager can open the review for a given employee
export const canOpenManagerReview = async (employeeEmail, id) => {
  const existing = await getAnnualReview(employeeEmail, id);
  if (!existing || !existing.review) return false;
  const item = existing.review;
  const userResp = item.user_response?.S ?? item.user_response ?? '';
  return !!userResp;
};
